import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { channelsService } from '../../services/channels.service'
import { getErrorMessage } from '../../types'
import toast from 'react-hot-toast'

interface AddChannelModalProps {
  onClose: () => void
}

export default function AddChannelModal({ onClose }: AddChannelModalProps) {
  const queryClient = useQueryClient()
  const [username, setUsername] = useState('')
  const [keywords, setKeywords] = useState('')

  const addMutation = useMutation({
    mutationFn: () =>
      channelsService.addChannel({
        username: username.replace('@', '').trim(),
        keywords: keywords
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k.length > 0),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] })
      queryClient.invalidateQueries({ queryKey: ['channelStats'] })
      toast.success('Канал добавлен')
      onClose()
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim()) {
      toast.error('Введите username канала')
      return
    }
    addMutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-auto overflow-hidden">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Добавить канал
              </h3>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username канала *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    @
                  </span>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="channel_username"
                    className="input pl-8 w-full"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Введите username публичного канала Telegram
                </p>
              </div>

              {/* Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ключевые слова (опционально)
                </label>
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="ищу, нужен, требуется"
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Введите ключевые слова через запятую для фильтрации сообщений
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={addMutation.isPending}
                className="btn-primary"
              >
                {addMutation.isPending ? 'Добавление...' : 'Добавить'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
